import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  CreditCard,
  FileText,
  Search,
  Filter,
  Download,
  MoreVertical,
  Edit2,
  Trash2,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart3,
  ChevronDown,
  X,
  RefreshCw,
  Eye,
  Tag,
  Clock,
} from "lucide-react";
import TransactionModal from "../components/TransactionModal";
import TransactionTable from "./TransactionTable";
import CustomSelectDropdown from "../components/CustomSelectDropdown";
import api from "../api/axios";
import clsx from "clsx";

const LedgerDetail = () => {
  const { ledgerId } = useParams();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("expense");
  const [ledger, setLedger] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      await api.delete(`/transactions/${deleteConfirm.id}`);
      fetchLedgerData(); // Refresh
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete transaction");
    }
  };

  // Filters state
  const [filters, setFilters] = useState({
    type: "all",
    category: "all",
    paymentMode: "all",
    dateRange: "all",
    amountRange: "all",
    search: "",
  });

  const fetchLedgerData = async () => {
    try {
      setIsLoading(true);

      // Fetch ledger details
      const ledgerResponse = await api.get(`/ledgers/${ledgerId}`);
      const ledgerData = ledgerResponse.data.data || ledgerResponse.data;
      setLedger(ledgerData);

      // Fetch transactions for this ledger
      const transactionsResponse = await api.get(
        `/transactions/ledger/${ledgerId}`
      );
      const transactionsData =
        transactionsResponse.data.data || transactionsResponse.data;
      setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
    } catch (error) {
      console.error("Error fetching ledger data:", error);
      if (error.response?.status === 404) {
        navigate("/ledgers");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLedgerData();
  }, [ledgerId]);

  // Define RupeeIcon component
  const RupeeIcon = ({ size = 16, className }) => (
    <span className={className} style={{ fontSize: size, fontWeight: "bold" }}>
      ₹
    </span>
  );

  // Calculate statistics
  const stats = useMemo(() => {
    const incomeTransactions = transactions.filter((t) => t.type === "income");
    const expenseTransactions = transactions.filter(
      (t) => t.type === "expense"
    );

    const totalIncome = incomeTransactions.reduce(
      (sum, t) => sum + t.amount,
      0
    );
    const totalExpense = expenseTransactions.reduce(
      (sum, t) => sum + t.amount,
      0
    );
    const netBalance = totalIncome - totalExpense;

    // Average transaction amounts
    const avgIncome =
      incomeTransactions.length > 0
        ? totalIncome / incomeTransactions.length
        : 0;
    const avgExpense =
      expenseTransactions.length > 0
        ? totalExpense / expenseTransactions.length
        : 0;

    // Category breakdown (top 5)
    const categoryBreakdown = {};
    transactions.forEach((t) => {
      if (!categoryBreakdown[t.category]) {
        categoryBreakdown[t.category] = { income: 0, expense: 0 };
      }
      if (t.type === "income") {
        categoryBreakdown[t.category].income += t.amount;
      } else {
        categoryBreakdown[t.category].expense += t.amount;
      }
    });

    // Monthly trend
    const monthlyData = {};
    transactions.forEach((t) => {
      const date = new Date(t.date);
      const monthYear = `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}`;
      if (!monthlyData[monthYear]) {
        monthlyData[monthYear] = { income: 0, expense: 0 };
      }
      if (t.type === "income") {
        monthlyData[monthYear].income += t.amount;
      } else {
        monthlyData[monthYear].expense += t.amount;
      }
    });

    return {
      totalIncome,
      totalExpense,
      netBalance,
      transactionCount: transactions.length,
      incomeCount: incomeTransactions.length,
      expenseCount: expenseTransactions.length,
      avgIncome,
      avgExpense,
      categoryBreakdown,
      monthlyData: Object.entries(monthlyData)
        .map(([month, data]) => ({ month, ...data }))
        .sort((a, b) => a.month.localeCompare(b.month)),
    };
  }, [transactions]);

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return transactions
      .filter((transaction) => {
        // Type filter
        if (filters.type !== "all" && transaction.type !== filters.type) {
          return false;
        }

        // Category filter
        if (
          filters.category !== "all" &&
          transaction.category !== filters.category
        ) {
          return false;
        }

        // Payment mode filter
        if (
          filters.paymentMode !== "all" &&
          transaction.paymentMode !== filters.paymentMode
        ) {
          return false;
        }

        // Amount range filter
        if (filters.amountRange !== "all") {
          const amount = transaction.amount;
          switch (filters.amountRange) {
            case "low":
              if (amount >= 1000) return false;
              break;
            case "medium":
              if (amount < 1000 || amount >= 10000) return false;
              break;
            case "high":
              if (amount < 10000) return false;
              break;
          }
        }

        // Search filter
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matches =
            transaction.category?.toLowerCase().includes(searchLower) ||
            transaction.notes?.toLowerCase().includes(searchLower) ||
            transaction.paymentMode?.toLowerCase().includes(searchLower);
          if (!matches) return false;
        }

        // Date range filter
        if (filters.dateRange !== "all") {
          const transactionDate = new Date(transaction.date);
          const now = new Date();
          const diffDays = Math.floor(
            (now - transactionDate) / (1000 * 60 * 60 * 24)
          );

          switch (filters.dateRange) {
            case "today":
              if (diffDays > 0) return false;
              break;
            case "week":
              if (diffDays > 7) return false;
              break;
            case "month":
              if (diffDays > 30) return false;
              break;
          }
        }

        return true;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date descending
  }, [transactions, filters]);

  // Get unique categories and payment modes for filters
  const uniqueCategories = useMemo(() => {
    const categories = new Set(
      transactions.map((t) => t.category).filter(Boolean)
    );
    return ["All", ...Array.from(categories)];
  }, [transactions]);

  const uniquePaymentModes = useMemo(() => {
    const modes = new Set(
      transactions.map((t) => t.paymentMode).filter(Boolean)
    );
    return ["All", ...Array.from(modes)];
  }, [transactions]);

  const handleAddIncome = () => {
    setModalType("income");
    setIsModalOpen(true);
  };

  const handleAddExpense = () => {
    setModalType("expense");
    setIsModalOpen(true);
  };

  const handleTransactionSuccess = () => {
    setIsModalOpen(false);
    fetchLedgerData();
  };

  const clearFilters = () => {
    setFilters({
      type: "all",
      category: "all",
      paymentMode: "all",
      dateRange: "all",
      amountRange: "all",
      search: "",
    });
  };

  const exportTransactions = () => {
    const csvContent = [
      ["Date", "Type", "Category", "Amount", "Payment Mode", "Notes"].join(","),
      ...filteredTransactions.map((t) =>
        [
          new Date(t.date).toLocaleDateString("en-IN"),
          t.type,
          t.category,
          t.amount,
          t.paymentMode,
          `"${t.notes || ""}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${ledger?.name || "book"}_transactions.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (window.confirm("Are you sure you want to delete this transaction?")) {
      try {
        await api.delete(`/transactions/${transactionId}`);
        fetchLedgerData();
      } catch (error) {
        console.error("Error deleting transaction:", error);
        alert("Failed to delete transaction");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading book details...</p>
        </div>
      </div>
    );
  }

  if (!ledger) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">Book not found</p>
        </div>
      </div>
    );
  }

  const dateRanges = [
    { value: "all", label: "All Time" },
    { value: "today", label: "Today" },
    { value: "week", label: "Last 7 Days" },
    { value: "month", label: "Last 30 Days" },
  ];

  const amountRanges = [
    { value: "all", label: "All Amounts" },
    { value: "low", label: "Below ₹1,000" },
    { value: "medium", label: "₹1,000 - ₹10,000" },
    { value: "high", label: "Above ₹10,000" },
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/ledgers")}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{ledger.name}</h1>
            <p className="text-gray-500 mt-0.5 text-sm">
              {ledger.description || "Manage income and expenses"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchLedgerData}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all duration-200 text-sm"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <div className="flex gap-2">
            <button
              onClick={handleAddIncome}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <Plus size={16} />
              Add Income
            </button>
            <button
              onClick={handleAddExpense}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <Plus size={16} />
              Add Expense
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Income</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">
                ₹{stats.totalIncome.toLocaleString("en-IN")}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {stats.incomeCount} transactions • Avg: ₹
                {stats.avgIncome.toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}
              </p>
            </div>
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Expense</p>
              <p className="text-2xl font-bold text-rose-600 mt-1">
                ₹{stats.totalExpense.toLocaleString("en-IN")}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {stats.expenseCount} transactions • Avg: ₹
                {stats.avgExpense.toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}
              </p>
            </div>
            <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="h-6 w-6 text-rose-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Net Balance</p>
              <p
                className={`text-2xl font-bold mt-1 ${
                  stats.netBalance >= 0 ? "text-emerald-600" : "text-rose-600"
                }`}
              >
                ₹{Math.abs(stats.netBalance).toLocaleString("en-IN")}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {stats.transactionCount} total transactions
              </p>
            </div>
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Book Status</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {ledger.status?.charAt(0).toUpperCase() +
                  ledger.status?.slice(1)}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Created {new Date(ledger.createdAt).toLocaleDateString("en-IN")}
              </p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <PieChart className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-lg">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search transactions..."
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-3">
            {/* Export Button */}
            <button
              onClick={exportTransactions}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all"
            >
              <Download size={16} />
              Export CSV
            </button>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                showFilters
                  ? "bg-indigo-600 text-white"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <Filter size={16} />
              Filters
              {Object.values(filters).some(
                (v, i) => v !== "all" && Object.keys(filters)[i] !== "search"
              ) && (
                <span className="bg-indigo-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {
                    Object.values(filters).filter(
                      (v, i) =>
                        v !== "all" && Object.keys(filters)[i] !== "search"
                    ).length
                  }
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-in fade-in slide-in-from-top-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">Filter Transactions</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Clear All
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilters({ ...filters, type: "all" })}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      filters.type === "all"
                        ? "bg-indigo-600 text-white"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilters({ ...filters, type: "income" })}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      filters.type === "income"
                        ? "bg-emerald-600 text-white"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Income
                  </button>
                  <button
                    onClick={() => setFilters({ ...filters, type: "expense" })}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      filters.type === "expense"
                        ? "bg-rose-600 text-white"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Expense
                  </button>
                </div>
              </div>

              {/* Category Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <CustomSelectDropdown
                  value={filters.category === "all" ? "" : filters.category}
                  options={uniqueCategories.filter((c) => c !== "All")}
                  onChange={(val) =>
                    setFilters({ ...filters, category: val || "all" })
                  }
                  placeholder="All Categories"
                  leftIcon={Tag}
                  optionIcon={Tag}
                />
              </div>

              {/* Payment Mode Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Payment Mode
                </label>
                <CustomSelectDropdown
                  value={
                    filters.paymentMode === "all" ? "" : filters.paymentMode
                  }
                  options={uniquePaymentModes.filter((m) => m !== "All")}
                  onChange={(val) =>
                    setFilters({ ...filters, paymentMode: val || "all" })
                  }
                  placeholder="All Modes"
                  leftIcon={CreditCard}
                  optionIcon={CreditCard}
                />
              </div>

              {/* Date Range */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Date Range
                </label>
                <CustomSelectDropdown
                  value={filters.dateRange === "all" ? "" : filters.dateRange}
                  options={dateRanges.map((r) => r.label)}
                  onChange={(val) => {
                    const selected = dateRanges.find((r) => r.label === val);
                    setFilters({
                      ...filters,
                      dateRange: selected ? selected.value : "all",
                    });
                  }}
                  placeholder="All Time"
                  leftIcon={Calendar}
                  optionIcon={Clock}
                />
              </div>
              {/* Amount Range */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Amount Range
                </label>
                <CustomSelectDropdown
                  value={
                    filters.amountRange === "all"
                      ? ""
                      : amountRanges.find(
                          (r) => r.value === filters.amountRange
                        )?.label || ""
                  }
                  options={amountRanges.map((r) => r.label)}
                  onChange={(val) => {
                    const selected = amountRanges.find((r) => r.label === val);
                    setFilters({
                      ...filters,
                      amountRange: selected ? selected.value : "all",
                    });
                  }}
                  placeholder="All Amounts"
                  leftIcon={RupeeIcon}
                  optionIcon={RupeeIcon}
                />
              </div>
            </div>

            {/* Active Filters */}
            {(filters.type !== "all" ||
              filters.category !== "all" ||
              filters.paymentMode !== "all" ||
              filters.dateRange !== "all" ||
              filters.amountRange !== "all") && (
              <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-gray-200">
                <span className="text-sm text-gray-600 font-medium">
                  Active filters:
                </span>
                {filters.type !== "all" && (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                    Type: {filters.type}
                    <button
                      onClick={() => setFilters({ ...filters, type: "all" })}
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                {filters.category !== "all" && (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                    {filters.category}
                    <button
                      onClick={() =>
                        setFilters({ ...filters, category: "all" })
                      }
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                {filters.paymentMode !== "all" && (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                    {filters.paymentMode}
                    <button
                      onClick={() =>
                        setFilters({ ...filters, paymentMode: "all" })
                      }
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                {filters.dateRange !== "all" && (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-violet-100 text-violet-700 text-xs font-medium rounded-full">
                    {
                      dateRanges.find((r) => r.value === filters.dateRange)
                        ?.label
                    }
                    <button
                      onClick={() =>
                        setFilters({ ...filters, dateRange: "all" })
                      }
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                {filters.amountRange !== "all" && (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-pink-100 text-pink-700 text-xs font-medium rounded-full">
                    {
                      amountRanges.find((r) => r.value === filters.amountRange)
                        ?.label
                    }
                    <button
                      onClick={() =>
                        setFilters({ ...filters, amountRange: "all" })
                      }
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Transactions Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Transactions ({filteredTransactions.length})
              <span className="text-sm font-normal text-gray-500 ml-2">
                • Total: ₹
                {filteredTransactions
                  .reduce(
                    (sum, t) =>
                      sum + (t.type === "income" ? t.amount : -t.amount),
                    0
                  )
                  .toLocaleString("en-IN")}
              </span>
            </h2>
            <div className="text-sm text-gray-500">
              Showing {filteredTransactions.length} of {transactions.length}{" "}
              transactions
            </div>
          </div>
        </div>

        <TransactionTable
          transactions={transactions}
          filteredTransactions={filteredTransactions}
          onView={(txn) => {
            setModalType(txn.type);
            setEditingTransaction(txn);
            setIsModalOpen(true);
          }}
          onEdit={(txn) => {
            setModalType(txn.type);
            setEditingTransaction(txn);
            setIsModalOpen(true);
          }}
          onDelete={(id) => {
            const txn = filteredTransactions.find(
              (t) => (t._id || t.id) === id
            );
            setDeleteConfirm({
              id,
              category: txn.category,
              amount: txn.amount,
            });
          }}
        />
      </div>

      {/* Transaction Modal */}
      <TransactionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTransaction(null);
        }}
        type={modalType}
        ledgerId={ledgerId}
        transaction={editingTransaction}
        onSuccess={handleTransactionSuccess}
      />

      {/* DELETE CONFIRMATION POPUP */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Delete Transaction?</h3>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={22} />
              </button>
            </div>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this transaction?
              <br />
              <span className="font-medium text-gray-900">
                "{deleteConfirm.category}"
              </span>{" "}
              of{" "}
              <span className="font-bold text-rose-600">
                ₹{deleteConfirm.amount.toLocaleString("en-IN")}
              </span>
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center gap-2"
              >
                <Trash2 size={18} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LedgerDetail;
